/**
 * The three snooze presets, as dates (GDN-3380).
 *
 * THE CLIENT NEVER COMPUTES THE INSTANT. The contract is explicit: the client
 * sends `YYYY-MM-DD` and the server resolves 09:00 in the organization's
 * timezone. A client that computed 09:00 itself would resolve it in the
 * BROWSER's zone, so the same preset would mean different things to a biller
 * in Chicago and their colleague travelling in Berlin, and the row would come
 * back at a time neither of them picked. The "9:00 AM" in the menu hint is
 * therefore a promise about the server's behaviour, not a number this file
 * produces.
 *
 * Local calendar arithmetic only. `toISOString()` is deliberately absent: it
 * converts to UTC first, which turns a late-evening "tomorrow" into today for
 * anyone west of Greenwich.
 */

export type SnoozePresetKey = 'tomorrow' | 'next_week' | 'in_30_days';

export interface SnoozePreset {
  key: SnoozePresetKey;
  /** The menu label. Exact copy; do not localize here. */
  label: string;
  /** The right-hand hint that says what the label resolves to. */
  hint: string;
  /** `YYYY-MM-DD`, the only thing that travels. */
  date: string;
}

/** `YYYY-MM-DD` from a Date's LOCAL calendar fields. */
export function toDateInput(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

/** The Monday after `from`. A Monday returns the Monday seven days later. */
function nextMonday(from: Date): Date {
  const daysUntilMonday = (8 - from.getDay()) % 7 || 7;
  return addDays(from, daysUntilMonday);
}

const weekdayShort = (date: Date) => date.toLocaleDateString('en-US', { weekday: 'short' });

const dayAndMonth = (date: Date) =>
  `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })}`;

export function buildSnoozePresets(today: Date = new Date()): SnoozePreset[] {
  const tomorrow = addDays(today, 1);
  const monday = nextMonday(today);
  const inThirtyDays = addDays(today, 30);

  return [
    {
      date: toDateInput(tomorrow),
      /* The one hint that names a time: it is the answer to "when tomorrow?",
         which is the only thing about this preset a biller cannot infer. */
      hint: `${weekdayShort(tomorrow)}, 9:00 AM`,
      key: 'tomorrow',
      label: 'Tomorrow',
    },
    {
      date: toDateInput(monday),
      hint: `${weekdayShort(monday)} ${dayAndMonth(monday)}`,
      key: 'next_week',
      label: 'Next week',
    },
    {
      date: toDateInput(inThirtyDays),
      hint: dayAndMonth(inThirtyDays),
      key: 'in_30_days',
      label: 'In 30 days',
    },
  ];
}

/** "10 Sep", for the snackbar. Parsed as a local date, not as UTC midnight. */
export function formatSnoozeDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return dayAndMonth(new Date(year, month - 1, day));
}
